import React, { useEffect, useCallback, useRef } from 'react';
import '../styles/ConfirmationModal.css';

interface ConfirmationModalProps {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
    error?: string | null;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onConfirm,
    onCancel,
    title = 'Confirmer la suppression',
    message = 'Cette action est irréversible. Êtes-vous sûr de vouloir continuer ?',
    confirmText = 'Supprimer',
    cancelText = 'Annuler',
    isLoading = false,
    error = null
}) => {
    // Track if mousedown started on overlay (not inside modal content)
    const mouseDownOnOverlay = useRef(false);

    // Handle escape key
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            onCancel();
        }
    }, [onCancel]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);

    // Only close if both mousedown and click happened on the overlay
    const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
        mouseDownOnOverlay.current = e.target === e.currentTarget;
    }, []);

    const handleOverlayClick = useCallback((e: React.MouseEvent) => {
        if (mouseDownOnOverlay.current && e.target === e.currentTarget) {
            onCancel();
        }
        mouseDownOnOverlay.current = false;
    }, [onCancel]);

    if (!isOpen) return null;

    return (
        <div className="confirmation-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
            <div
                className="confirmation-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <div className="confirmation-modal-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </div>
                <h3 id="modal-title" className="confirmation-modal-title">{title}</h3>
                <p className="confirmation-modal-message">{message}</p>
                {error && (
                    <div className="confirmation-modal-error" role="alert">
                        {error}
                    </div>
                )}
                <div className="confirmation-modal-actions">
                    <button
                        type="button"
                        className="confirmation-modal-btn confirmation-modal-btn-cancel"
                        onClick={onCancel}
                        disabled={isLoading}
                        autoFocus
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className="confirmation-modal-btn confirmation-modal-btn-confirm"
                        onClick={onConfirm}
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? 'Suppression...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};
